import { DataType, TableName } from "../../../tables"
import { round } from "../../../utils/typeUtils"
import { useManagerHooks } from "../../manager"
import { Item, PaymentStatus, usePricesHooks, usePriceOutlineHooks, operationHelpers } from "../../operations"
import { useCustomerLoaders } from "../loaders/customerLoaders"
import { useHoardLoaders } from "../loaders/hoardLoaders"
import { HoardOrder, HoardType, SaleOperation } from "../state/types"
import { ListResponse } from "../../common"
import { Params } from "../../../state/reducers/types"
import { Data2 } from "../../../features/data-types"
import { HoardFilters, HoardStatusFilter } from "./types"
import { ValueFilter } from "../../../services/dynamo"
import { FiscalDocumentStatus, isInvoiceableFiscalDocumentStatus, isUninvoicedFiscalDocumentStatus } from "../../fiscal-documents"
import { DeliveryOrder, DeliveryStatus } from "../../deliveries"
import { useCreditPaymentHooks } from "../../administration"
import { O } from "../../../features/ui"
import {
    getInvoicedQuantity,
    getInvoiceableItems,
    getNetDeliveredByItem,
    hasInvoicedPendingItems,
    splitQuantitiesByInvoicing
} from "../utils/hoardInvoiceHelpers"

export type { InvoiceableItem, InvoiceableItems, InvoicedSplit } from "../utils/hoardInvoiceHelpers"

export const useHoardHooks = () => {
    const { describeNoun } = operationHelpers
    const { currentBranchId } = useManagerHooks()
    const pricesHooks = usePricesHooks()
    const { getAdjustedTotal } = usePriceOutlineHooks()
    const { getCustomerDebtData } = useCreditPaymentHooks()

    const hoardLoaders = useHoardLoaders()
    const customerLoaders = useCustomerLoaders()

    const getUsedCredit = (items: Item[]) => {
        return round(items.reduce((sum, item) =>
            sum + (pricesHooks.adjustment.apply(item.price, -(item.specialDiscountYield || 0)) * (item.quantity - (item.cancelledQuantity || 0)))
        , 0))
    }

    const getPendingCredit = (hoard: HoardOrder, additionalItems?: Item[]) => { 
        return round(hoard.value! - (hoard.unusedCredit || 0) - getUsedCredit(hoard.items) - getUsedCredit(additionalItems || []))
    }

    // Cuando lo utilizado supera el valor del acopio, el valor se amplía a lo utilizado y la diferencia queda pendiente de pago.
    // paymentStatus se recalcula sobre el nuevo total: si no se había pagado nada queda como estaba (cuenta corriente, contra entrega)
    const getExtendedValue = (hoard: HoardOrder, additionalItems?: Item[]) => {
        const pendingCredit = getPendingCredit(hoard, additionalItems)
        if (pendingCredit >= 0) {
            return undefined
        }
        const value = round(hoard.value! - pendingCredit)
        const paidAmount = hoard.paidAmount || 0
        const total = getAdjustedTotal(TableName.HOARD_ORDERS, { ...hoard, value })
        const paymentStatus = paidAmount === 0 ? hoard.paymentStatus :
            paidAmount >= total ? PaymentStatus.PAYED : PaymentStatus.PARTIALLY_PAYED
        return {
            value,
            paymentStatus,
            pendingPayment: Math.max(0, round(total - paidAmount)),
            // Lo que se le cobra (o se carga a cuenta corriente) por la ampliación: con el recargo del acopio
            addedAmount: round(pricesHooks.adjustment.apply(value - hoard.value!, hoard.paymentTypeYield || 0))
        }
    }

    // Crédito disponible en la cuenta corriente del cliente (undefined si no tiene): el crédito a favor se suma al límite
    const getAvailableCredit = async (customerId: string, dbRequest = false): Promise<O<number>> => {
        const { loadCustomer } = customerLoaders()
        const customer = await loadCustomer(customerId, dbRequest)
        if (!customer?.creditAccount) return undefined
        const debtData = await getCustomerDebtData(customer.id, dbRequest)
        return round(customer.creditAccount.creditLimit - debtData.debt + Math.max(0, customer.creditInFavor || 0))
    }

    // Restante de un acopio financiero al cerrarlo. Si se acredita, una nota de crédito interna (noteAmount, con el ajuste
    // del medio de pago) baja lo adeudado a lo utilizado y lo pagado por encima de eso pasa al crédito a favor del cliente
    const getClosingCredit = (hoard: HoardOrder) => {
        const pendingCredit = Math.max(0, getPendingCredit(hoard))
        const noteAmount = round(pricesHooks.adjustment.apply(pendingCredit, hoard.paymentTypeYield || 0))
        const usedTotal = round(getAdjustedTotal(TableName.HOARD_ORDERS, hoard) - noteAmount)
        return {
            pendingCredit,
            noteAmount,
            creditInFavor: Math.max(0, round((hoard.paidAmount || 0) - usedTotal))
        }
    }

    // Con IVA (igual que hoard.pricesList, con el que se consume) y sin el ajuste del medio de pago
    const getHoardValue = (
        payedAmount: number,
        paymentTypeYield = 0
    ) => {
        return pricesHooks.adjustment.remove(payedAmount, paymentTypeYield)
    }

    // Acopio con el valor ampliado: con el recargo del acopio, su total sube exactamente addedAmount
    const getIncreasedHoard = (hoard: HoardOrder, addedAmount: number): HoardOrder => ({
        ...hoard,
        value: (hoard.value || 0) + getHoardValue(addedAmount, hoard.paymentTypeYield)
    })

    const pricesListFileName = (hoard: HoardOrder) => {
        return `Lista-Precios_${describeNoun(TableName.HOARD_ORDERS)}-${hoard.code}`
    }

    const loadHoardsBatch = async (
        filtersData: Data2 = {},
        batchSize?: number,
        startKey?: Params
    ): Promise<ListResponse<HoardOrder>> => {
        const { searchText, deliveryStatus, paymentStatus, sellerId, customerId, dateFrom, dateTo, type, hoardStatus } = filtersData as HoardFilters
        
        const sort = {
            index: 'CodeIndex',
            prop: 'code',
            asc: false
        }
        const textFilters = searchText?.trim() ?
            [{ prop: 'codeStr', text: searchText.trim() }] :
            undefined

        const valueFilters: ValueFilter[] = [
            { prop: 'branchId', value: currentBranchId! },
            { prop: 'archived', value: true, equals: false },
            { prop: 'cancelled', value: true, equals: false }
        ]
        deliveryStatus && valueFilters.push({ prop: 'deliveryStatus', value: deliveryStatus })
        // Si se eligió un estado de entrega puntual, ese filtro manda sobre activo/cerrado
        if (!deliveryStatus && hoardStatus) {
            valueFilters.push({ prop: 'deliveryStatus', value: DeliveryStatus.DELIVERED, equals: hoardStatus === HoardStatusFilter.CLOSED })
        }
        paymentStatus && valueFilters.push({ prop: 'paymentStatus', value: paymentStatus })
        sellerId && valueFilters.push({ prop: 'sellerId', value: sellerId })
        customerId && valueFilters.push({ prop: 'customerId', value: customerId })
        type && valueFilters.push({ prop: 'type', value: type })

        const dateTimeFrom = dateFrom
        dateTimeFrom?.setHours(0, 0, 0, 0)
        const dateTimeTo = dateTo
        dateTimeTo?.setHours(23, 59, 59, 999)
        const rangeFilters = dateTimeFrom || dateTimeTo ?
            [{
                prop: 'dueDate',
                ...(dateTimeFrom && { start: dateTimeFrom.toISOString() }),
                ...(dateTimeTo && { end: dateTimeTo.toISOString() })
            }] :
            undefined
        
        return hoardLoaders().loadHoardsBatch({
            startKey,
            limit: batchSize,
            sort,
            textFilters,
            valueFilters,
            rangeFilters
        })
    }

    const loadBatchData = async (hoardsDT: DataType[]) => {
        const hoards = hoardsDT as HoardOrder[]
        const { loadCustomers } = customerLoaders()
        await loadCustomers(hoards.map(hoard => hoard.customerId))
    }

    return {
        getUsedCredit,
        getPendingCredit,
        getExtendedValue,
        getAvailableCredit,
        getClosingCredit,
        getHoardValue,
        getIncreasedHoard,
        pricesListFileName,
        loadHoardsBatch,
        loadBatchData
    }
}

export const hoardHelpers = {
    isFinancialHoard: (table: TableName, operation: SaleOperation): boolean => {
        return table === TableName.HOARD_ORDERS && (operation as HoardOrder).type === HoardType.FINANCIAL
    },

    // Financial hoard items split by their delivery situation, taken from the delivery orders stats:
    // delivered (everything ever delivered, including what was later returned), pending (emitted but not delivered yet)
    // and returned. delivered - returned + pending = used credit
    getFinancialHoardItemsByStatus: (hoard: HoardOrder, deliveryOrders: DeliveryOrder[]) => {
        const totals = new Map<string, { delivered: number, pending: number, returned: number }>()
        deliveryOrders.forEach(deliveryOrder => deliveryOrder.items.forEach(orderItem => {
            const { delivered, dispatched, pending, returned = 0 } = orderItem.deliveryStats
            const itemTotals = totals.get(orderItem.itemId) || { delivered: 0, pending: 0, returned: 0 }
            totals.set(orderItem.itemId, {
                delivered: itemTotals.delivered + delivered + returned,
                pending: itemTotals.pending + pending + dispatched,
                returned: itemTotals.returned + returned
            })
        }))
        const itemsWith = (status: 'delivered' | 'pending' | 'returned'): Item[] => hoard.items
            .map(item => ({ ...item, quantity: totals.get(item.id)?.[status] || 0, cancelledQuantity: undefined }))
            .filter(item => item.quantity > 0)
        return {
            delivered: itemsWith('delivered'),
            pending: itemsWith('pending'),
            returned: itemsWith('returned')
        }
    },

    isInvoiceable: (hoard: HoardOrder): boolean => {
        const { invoiceStatus, cancelled, archived } = hoard
        return isInvoiceableFiscalDocumentStatus(invoiceStatus) && !cancelled && !archived
    },

    getNetDeliveredByItem,
    hasInvoicedPendingItems,
    getInvoiceableItems,
    getInvoicedQuantity,
    splitQuantitiesByInvoicing,

    isClosable: (hoard: HoardOrder): boolean => {
        const { type, items, deliveryStatus, cancelled, archived } = hoard
        const isFinancial = type === HoardType.FINANCIAL
        const hasPendingItems = items.some(item => item.deliveryStats.pending > 0)
        const isNotDelivered = deliveryStatus !== DeliveryStatus.DELIVERED
        return (isFinancial ? isNotDelivered : hasPendingItems) && !cancelled && !archived
    },

    // Un acopio financiero sin pagos, sin facturar y sin entregas vigentes se puede cancelar como si nunca hubiera
    // existido: no hay plata, stock ni documentos que revertir. Las entregas anuladas no lo impiden porque al anularlas
    // ya se cancelaron sus productos y se liberó el stock (y solo se marcan canceladas si no tuvieron movimientos)
    isFinancialHoardCancellable: (hoard: HoardOrder, deliveryOrders: DeliveryOrder[]): boolean => {
        const { type, paymentStatus, invoiceStatus, items, cancelled, archived } = hoard
        return type === HoardType.FINANCIAL &&
            paymentStatus === PaymentStatus.PENDING &&
            isUninvoicedFiscalDocumentStatus(invoiceStatus) &&
            deliveryOrders.every(deliveryOrder => deliveryOrder.cancelled) &&
            items.every(item => (item.cancelledQuantity || 0) >= item.quantity) &&
            !cancelled && !archived
    }
}
